import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, IconButton } from '@socialchorus/shared-ui-components';
import Spinner from '../../../../components/ui/spinner';
import './link-input.scss';

type LinkInputProps = {
  isFetching: boolean;
  onSubmit: (url: string) => void;
  onClose: () => void;
};

const LinkInput = ({ isFetching, onSubmit, onClose }: LinkInputProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || isFetching) return;
    onSubmit(trimmed);
  }, [value, isFetching, onSubmit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [submit, onClose]
  );

  const canSubmit = value.trim().length > 0 && !isFetching;

  return (
    <div className="ugc-link-input">
      <div className="ugc-link-input__field">
        <span className="ugc-link-input__favicon">
          {isFetching ? (
            <Spinner loading />
          ) : (
            <Icon size={20} aria-hidden="true">
              language
            </Icon>
          )}
        </span>
        <input
          ref={inputRef}
          type="url"
          className="ugc-link-input__input"
          placeholder={t(
            'user_generated_content.media.links.input_placeholder'
          )}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isFetching}
          aria-label={t('user_generated_content.media.links.add')}
        />
        <IconButton
          iconName="send"
          size="compact"
          onClick={submit}
          disabled={!canSubmit}
          aria-label={t('user_generated_content.media.links.submit')}
        />
      </div>
      <IconButton
        iconName="close"
        size="compact"
        onClick={onClose}
        aria-label={t('user_generated_content.media.links.close')}
      />
    </div>
  );
};

export default LinkInput;
